import { graphqlRequest, graphqlUpload } from "./client";
import { CHECK_FIELDS, GATE_FIELDS, type CheckItem, type StepGate } from "./types";

export interface PackageView {
  step: {
    portfolioUrl?: string | null;
    portfolioOk: boolean;
    selfCheck: string[];
    cvName?: string | null;
    cvSize?: number | null;
    hasCv: boolean;
  };
  portfolioChecks: CheckItem[];
  /** Self-check points ticked. Optional: a guide to what clients look for. */
  selfCount: number;
  gate: StepGate;
  done: boolean;
}

const PACKAGE_FIELDS = `step { portfolioUrl portfolioOk selfCheck cvName cvSize hasCv }
  portfolioChecks { ${CHECK_FIELDS} } selfCount ${GATE_FIELDS} done`;

/** Step 2: Package. */
export async function fetchPackage(): Promise<PackageView> {
  return (await graphqlRequest<{ getPackage: PackageView }>(`{ getPackage { ${PACKAGE_FIELDS} } }`))
    .getPackage;
}

/** Verify the portfolio link. */
export async function verifyPortfolio(
  url: string,
): Promise<{ view: PackageView; checks: CheckItem[]; keptPrevious: boolean }> {
  return (
    await graphqlRequest<{
      verifyPortfolio: { view: PackageView; checks: CheckItem[]; keptPrevious: boolean };
    }>(
      `mutation($url: String!) { verifyPortfolio(url: $url) { keptPrevious checks { ${CHECK_FIELDS} } view { ${PACKAGE_FIELDS} } } }`,
      { variables: { url } },
    )
  ).verifyPortfolio;
}

/** Save the 10-point self-check. */
export async function saveSelfCheck(keys: string[]): Promise<PackageView> {
  return (
    await graphqlRequest<{ saveSelfCheck: PackageView }>(
      `mutation($keys: [String!]!) { saveSelfCheck(keys: $keys) { ${PACKAGE_FIELDS} } }`,
      { variables: { keys } },
    )
  ).saveSelfCheck;
}

/** Upload the CV (PDF or Word, under 8MB). */
export async function uploadCv(file: File): Promise<PackageView> {
  return (
    await graphqlUpload<{ uploadCv: PackageView }>(
      `mutation($file: Upload!) { uploadCv(file: $file) { ${PACKAGE_FIELDS} } }`,
      {},
      "file",
      file,
    )
  ).uploadCv;
}

/** Short-lived link to the participant's own CV. */
export async function fetchCvUrl(): Promise<string> {
  return (await graphqlRequest<{ getCvUrl: string }>(`{ getCvUrl }`)).getCvUrl;
}

/** Complete Step 2. */
export async function completePackage(): Promise<PackageView> {
  return (
    await graphqlRequest<{ completePackage: PackageView }>(
      `mutation { completePackage { ${PACKAGE_FIELDS} } }`,
    )
  ).completePackage;
}
